import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import {
  useUpsertTicketMutation,
  useLazyGetTicketByIdQuery,
  TicketUpsertRequest,
  TicketView,
  TicketConflictError,
} from '../../../services/TicketService';
import { fromLocalDateString } from '../../../utils/DateConversions';
import { ConflictState, FieldChoice } from '../components/TicketForm/actions/ConflictResolutionDialog';
import { isFetchBaseQueryError, parseFieldValue, setByBackendPath } from '../components/TicketForm/actions/ticketConflictUtils';

export type SaveStatus = { type: 'success' | 'error'; message: string } | null;

const CONCURRENCY_CONFLICT_MESSAGE =
  'This ticket changed again before your save completed. Please try again.';

/** Applies the "Due Date is a plain date; push to end-of-day for the API" convention. */
function buildUpdatedView(ticket: TicketView): TicketView {
  const { dueDate } = ticket.timeTracking;
  return dueDate
    ? { ...ticket, timeTracking: { ...ticket.timeTracking, dueDate: fromLocalDateString(dueDate) } }
    : ticket;
}

/**
 * Encapsulates the ticket-save + spec-010 conflict-resolution flow (state, upsert call, 409
 * handling) so `SaveTicketButton` itself stays focused on validation + rendering.
 * ticketSlice's extraReducers refresh currentTicket/baseTicket on a successful upsert.
 */
export function useSaveTicket() {
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const { currentTicket, baseTicket } = useAppSelector(state => state.ticket);
  const [upsertTicket] = useUpsertTicketMutation();
  const [fetchTicket] = useLazyGetTicketByIdQuery();
  const navigate = useNavigate();

  const submitUpsert = async (request: TicketUpsertRequest) => {
    const result = await upsertTicket(request).unwrap();
    if (!currentTicket?.id && result.id) {
      navigate(`/tickets/edit?id=${result.id}&fromCreate=true`);
    }
    setSaveStatus({ type: 'success', message: 'Ticket saved successfully.' });
  };

  const handleSaveError = (error: unknown) => {
    const data = isFetchBaseQueryError(error) && error.status === 409 ? (error.data as TicketConflictError) : undefined;
    if (!data) {
      setSaveStatus({ type: 'error', message: 'Failed to save ticket. Please try again.' });
      return;
    }
    if (!data.conflicts?.length) {
      setSaveStatus({ type: 'error', message: CONCURRENCY_CONFLICT_MESSAGE });
      return;
    }
    setConflict({
      conflicts: data.conflicts,
      choices: Object.fromEntries(data.conflicts.map(c => [c.path, 'mine' as FieldChoice])),
    });
  };

  const save = async () => {
    if (!currentTicket) return;
    setIsSaving(true);
    try {
      await submitUpsert({ base: baseTicket, updated: buildUpdatedView(currentTicket) });
    } catch (error) {
      handleSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const setConflictChoice = (path: string, choice: FieldChoice) =>
    setConflict(prev => (prev ? { ...prev, choices: { ...prev.choices, [path]: choice } } : prev));

  const cancelConflict = () => setConflict(null);

  const confirmConflict = async () => {
    if (!conflict || !currentTicket) return;
    setIsSaving(true);
    try {
      const fresh = await fetchTicket(currentTicket.id).unwrap();
      const merged: TicketView = JSON.parse(JSON.stringify(fresh));
      conflict.conflicts.forEach(fieldConflict => {
        if (conflict.choices[fieldConflict.path] === 'mine') {
          setByBackendPath(merged, fieldConflict.path, parseFieldValue(fieldConflict.incomingValue));
        }
      });
      await submitUpsert({ base: fresh, updated: merged });
      setConflict(null);
    } catch (error) {
      handleSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    saveStatus,
    setSaveStatus,
    conflict,
    save,
    setConflictChoice,
    cancelConflict,
    confirmConflict,
  };
}
